import { Descriptions, Grid, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type GiftVoucherModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/GiftVoucherModuleWithPermissionsSpecs';
import { ContentRefinementSourceAction, ContentRefinementSourceKind } from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import GiftVoucherModuleAction from './GiftVoucherModuleAction';
import GiftVoucherModuleDetailsInner from './GiftVoucherModuleDetailsInner';
import GiftVoucherModuleFooter from './GiftVoucherModuleFooter';
import { HeaderTabs } from './shared';

export type GiftVoucherModulePageProps = { module: GiftVoucherModuleWithPermissionsSpecsFragment };

const GiftVoucherModulePage = ({ module }: GiftVoucherModulePageProps) => {
    const { t } = useTranslation(['']);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.MainDetails);

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: permissions.hasUpdatePermission,
            source: {
                kind: ContentRefinementSourceKind.Module,
                action: ContentRefinementSourceAction.Update,
                identifier: module.id,
            },
        }),
        [module.id, permissions.hasUpdatePermission]
    );

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('giftVoucherModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>

            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <ConsolePageWithHeader
                content={content}
                extra={<GiftVoucherModuleAction module={module} permissions={permissions} />}
                footer={[<GiftVoucherModuleFooter activeTab={activeTab} />]}
                header={{
                    footer: (
                        <Tabs
                            activeKey={activeTab}
                            items={[
                                {
                                    label: t('giftVoucherModuleDetails:tabs.mainDetails.title'),
                                    key: HeaderTabs.MainDetails,
                                },
                                {
                                    label: t('giftVoucherModuleDetails:tabs.email.title'),
                                    key: HeaderTabs.Email,
                                },
                            ]}
                            onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}
                        />
                    ),
                }}
                onBack={() => navigate('/admin/system/modules')}
                title={
                    screens.md ? t('giftVoucherModuleDetails:title', { name: module.displayName }) : module.displayName
                }
            >
                <GiftVoucherModuleDetailsInner activeTab={activeTab} module={module} />
            </ConsolePageWithHeader>
        </GenericStateOnPageContextProvider>
    );
};

export default GiftVoucherModulePage;
